import apiClient from './api.service';
import {
  AiCheckout,
  AiPlans,
  AiSearchMode,
  AiAutomationResult,
  AiSearchResult,
  AiSearchTurn,
  AiStatus,
  AiWorkspace,
  AssistantAnswers,
  AssistantMode,
  AssistantReply,
  AssistantExecuteResult,
  AssistantActionParams,
  AssistantHistoryTurn
} from '../types/ai.types';
import {
  AssistantPayload,
  StreamStep,
  assistantWithFallback,
  fetchAssistantStream
} from './assistantStream';

class AiService {
  async getStatus(storeId: number): Promise<AiStatus> {
    const response = await apiClient.get<{ success: boolean; data: AiStatus }>(
      `/stores/${storeId}/ai/status`
    );
    return response.data.data;
  }

  async getWorkspace(storeId: number): Promise<AiWorkspace> {
    const response = await apiClient.get<{ success: boolean; data: AiWorkspace }>(
      `/stores/${storeId}/ai/workspace`
    );
    return response.data.data;
  }

  async activate(storeId: number): Promise<void> {
    await apiClient.post(`/stores/${storeId}/ai/activate`);
  }

  async updateConfig(
    storeId: number,
    patch: { model_id?: number | null; data_access?: string }
  ): Promise<void> {
    await apiClient.put(`/stores/${storeId}/ai/config`, patch);
  }

  async getPlans(storeId: number): Promise<AiPlans> {
    const response = await apiClient.get<{ success: boolean; data: AiPlans }>(
      `/stores/${storeId}/ai/plans`
    );
    return response.data.data;
  }

  async checkout(storeId: number, tier: string): Promise<AiCheckout> {
    const response = await apiClient.post<{ success: boolean; data: AiCheckout }>(
      `/stores/${storeId}/ai/checkout`,
      { tier }
    );
    return response.data.data;
  }

  async search(
    storeId: number,
    payload: { prompt: string; history?: AiSearchTurn[]; current_tree?: unknown; mode?: AiSearchMode }
  ): Promise<AiSearchResult> {
    const response = await apiClient.post<{ success: boolean; data: AiSearchResult }>(
      `/stores/${storeId}/ai/search`,
      payload,
      { timeout: 60000 }
    );
    return response.data.data;
  }

  async automation(
    storeId: number,
    payload: {
      prompt: string;
      history?: AiSearchTurn[];
      workflow?: unknown;
      mode?: AiSearchMode;
    }
  ): Promise<AiAutomationResult> {
    const response = await apiClient.post<{ success: boolean; data: AiAutomationResult }>(
      `/stores/${storeId}/ai/automation`,
      payload,
      { timeout: 60000 }
    );
    return response.data.data;
  }

  async assistant(
    storeId: number,
    payload: {
      message: string;
      mode?: AssistantMode;
      history?: AssistantHistoryTurn[];
      page?: string;
      thread_id?: string;
      answers?: AssistantAnswers;
    }
  ): Promise<AssistantReply> {
    const response = await apiClient.post<{ success: boolean; data: AssistantReply }>(
      `/stores/${storeId}/ai/assistant`,
      payload,
      { timeout: 60000 }
    );
    return response.data.data;
  }

  async assistantLive(
    storeId: number,
    payload: AssistantPayload,
    handlers: { onStep?: (step: StreamStep) => void; onToken?: (text: string) => void } = {}
  ): Promise<AssistantReply> {
    return assistantWithFallback(
      (id, body) => fetchAssistantStream(id, body, handlers),
      (id, body) => this.assistant(id, body),
      storeId,
      payload
    );
  }

  async assistantExecute(
    storeId: number,
    payload: { action_id: string; params?: AssistantActionParams; thread_id?: string }
  ): Promise<AssistantExecuteResult> {
    const response = await apiClient.post<{ success: boolean; data: AssistantExecuteResult }>(
      `/stores/${storeId}/ai/assistant/execute`,
      payload,
      { timeout: 60000 }
    );
    return response.data.data;
  }
}

export default new AiService();
